import type { IAutocompleteProps } from "@similarweb/ui-components";
import { DotsLoader, ListItemSeparator, AutocompleteWebsiteItem } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import { translate } from "app/filters/i18nFilter";
import { getWebsiteResults } from "app/services/solutions2Services/HomepageDataFetchers/NewModulesHomepageDataFetcher";

import { ScrollAreaWrap } from "./components/ScrollAreaWrapper";
import {
    CustomIndicator,
    ListExamplesHeader,
    ListItemWebsitePartialSelectionStyled,
    MainSiteAutocompleteStyled,
    StyledTextField,
} from "./styles";
import type { IAutocompleteWebsitesMainItemProps, IMainItem, IWebsitePageResult, WebsitePageSearchType } from "./types";
import { getUrlComponents } from "./urlComponentUtils";
import {
    filterOutBlockList,
    filterOutExcludes,
    filterOutQuery,
    getCustomIndicator,
    getListItemsGroupsContent,
    getPageLevelAutoCompleteItems,
    getPageLevelAutocompleteRecents,
    handleGetListItems,
    renderItemCreator,
} from "./utils";

const FIRST_EXAMPLE_URL = {
    name: "aws.amazon.com",
    searchType: "subdomain" as WebsitePageSearchType,
    fullUrl: "aws.amazon.com",
};
const SECOND_EXAMPLE_URL = {
    name: "amazon.com",
    searchType: "folder" as WebsitePageSearchType,
    fullUrl: "booking.com/hotel",
};

export const AutocompleteWebsitesPageMainItem = ({
    onClick,
    onClose,
    recentSearches,
    defaultValue,
    defaultSearchType,
    excludes = [],
    autocompleteProps,
    className,
    onKeyUp,
    onArrowKeyPress,
    overrideEnterFunc,
    isDisabled,
    disablePlaceHolderKey = "websitePages.autocomplete.disable.placeholder",
    showCompareSites,
}: IAutocompleteWebsitesMainItemProps) => {
    const onItemClick = (item: IWebsitePageResult, type: "query" | "recent") => () => {
        onClick(item, type);
    };

    const getListItems = async (query: string) => {
        let pageLevelItems: FixmeType[] = [];
        const { domain, subdomain, folder, queryParams } = getUrlComponents(query);
        const { recents, hasNoRecentsData } = getPageLevelAutocompleteRecents(
            recentSearches,
            excludes,
            showCompareSites ?? false,
            onItemClick as FixmeType,
        );

        if (domain) {
            pageLevelItems = await getPageLevelAutoCompleteItems(
                onItemClick,
                query,
                domain,
                subdomain,
                folder,
                queryParams,
                hasNoRecentsData || query !== defaultValue ? [] : (recents.slice(0, 3) as IWebsitePageResult[]),
                excludes,
            );

            if (folder?.length > 1 || queryParams?.length) {
                return pageLevelItems;
            }
        }

        if (query?.length) {
            try {
                const queries = await getWebsiteResults(query);
                if (queries.length === 0) {
                    return [autocompleteStates.NO_DATA_SEARCHES];
                }

                let results = queries
                    .filter(filterOutExcludes(excludes))
                    .filter(filterOutBlockList)
                    // @ts-ignore FIXME: strict migration - onItemClick returns () => void but renderItemCreator expects void return
                    .map(renderItemCreator("query", onItemClick));

                if (pageLevelItems.length) {
                    // @ts-ignore FIXME: strict migration - results shape after renderItemCreator doesn't match ISite
                    results = results.filter(filterOutQuery(query));
                }

                return [...pageLevelItems, ...results];
            } catch (e) {
                return [autocompleteStates.ERROR];
            }
        }

        if (hasNoRecentsData) {
            return recentSearches;
        }

        return recents;
    };

    const renderItems = ({
        selectedItemId,
        listItems,
    }: // @ts-ignore FIXME: strict migration - IAutocompleteProps["renderItems"] may be undefined — extracting Parameters from optional function
    Parameters<IAutocompleteProps["renderItems"]>[0] & {
        listItems: Array<IMainItem | autocompleteStates>;
    }) => {
        if (!listItems.length) {
            return null;
        }

        const itemCreator = (item: IMainItem, index: number) => {
            const { name, image, icon } = item;
            if (!item.compareItems) {
                return (
                    <ListItemWebsitePartialSelectionStyled
                        key={`${name}_${index}`}
                        isActive={index === selectedItemId}
                        text={name}
                        secondaryText={item.secondaryText}
                        onClick={() => item.props.onClick(item)}
                        selectedTextIndexes={item.selectedTextIndexes}
                        content={item.afterElementText}
                        reverseEllipsis={item.reverseEllipsis}
                        img={icon ?? image}
                    />
                );
            }
            return (
                <AutocompleteWebsiteItem
                    key={`${item.mainSite.domain}_compare_${index}`}
                    maxCompareItemsToDisplay={1}
                    mainSite={item.mainSite}
                    compareSites={item.compareItems}
                    itemUrl={image}
                    onItemClick={() => item.props.onClick(item)}
                />
            );
        };

        const { recents, searchResults } = getListItemsGroupsContent({
            listItems,
            renderListItem: itemCreator,
        });

        const onExampleClick = (itemUrl: typeof FIRST_EXAMPLE_URL) => () => {
            onClick(itemUrl, "query");
        };

        return (
            <div className="ListItemsContainer">
                <ListExamplesHeader>
                    {translate("autocomplete.websitePagesMain.example.links.title")}
                    &nbsp;
                    <a onClick={onExampleClick(FIRST_EXAMPLE_URL)}>{FIRST_EXAMPLE_URL.fullUrl}</a>
                    &nbsp;
                    {translate("autocomplete.websitePagesMain.example.links.or")}
                    &nbsp;
                    <a onClick={onExampleClick(SECOND_EXAMPLE_URL)}>{SECOND_EXAMPLE_URL.fullUrl}</a>
                </ListExamplesHeader>
                <Stack gap="8px">
                    {searchResults.length > 0 ? <ScrollAreaWrap>{searchResults}</ScrollAreaWrap> : null}
                    {recents.length > 0 ? (
                        <section>
                            <ListItemSeparator>
                                {translate("autocomplete.websitePagesMain.seperator.text.recents")}
                            </ListItemSeparator>
                            <ScrollAreaWrap>{recents}</ScrollAreaWrap>
                        </section>
                    ) : null}
                </Stack>
            </div>
        );
    };

    const getAutocompleteIndicator = (query: string) => {
        const customIndicator = getCustomIndicator(defaultSearchType, defaultValue)(query);
        return <CustomIndicator>{customIndicator}</CustomIndicator>;
    };

    return isDisabled ? (
        <StyledTextField
            disabled={true}
            placeholder={translate(disablePlaceHolderKey)}
        />
    ) : (
        <MainSiteAutocompleteStyled
            placeholder={translate("autocomplete.websitePages.main.item.placeholder")}
            className={className}
            isFocused={true}
            autoFocus={true}
            width={500}
            getListItems={handleGetListItems(defaultSearchType ?? "domain", defaultValue ?? "", getListItems)}
            selectedValue={defaultValue}
            renderItems={renderItems}
            loadingComponent={<DotsLoader />}
            preventTruncateUnlessForced={true}
            resetValueOnSelect={true}
            floating={true}
            debounce={250}
            maxResults={20}
            onKeyUp={onKeyUp}
            onArrowKeyPress={onArrowKeyPress}
            overrideEnterFunc={overrideEnterFunc}
            getCustomIndicator={getAutocompleteIndicator}
            {...autocompleteProps}
        />
    );
};
